import { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import './RequestTable.css';
import { Avatar, Box, MenuItem, Select, Typography, Popover } from '@mui/material';

import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../../../utils/dateUtils';

const LevelToTeachColor = {
  university: {
    color: '#14B334',
    bgcolor: '#F2FFF5',
    value: 'UN',
    value2: 'University',
  },
  high_school: {
    color: '#E0AD14',
    bgcolor: '#FFFBF0',
    value: 'HS',
    value2: 'High School',
  },
  middle_school: {
    color: '#6B60E7',
    bgcolor: '#F0EFFF',
    value: 'MS',
    value2: 'Middle School',
  },
  elementary: {
    color: '#6B60E7',
    bgcolor: '#F0EFFF',
    value: 'ES',
    value2: 'Elementary School',
  },
};

const StatusColor = {
  approval_pending: { color: '#EBBF37', bgcolor: '#FFFAEC', value: 'Pending' },
  approved: { color: '#14B334', bgcolor: '#F2FFF5', value: 'Approved' },
  rejected: { color: '#E14949', bgcolor: '#FFF2F2', value: 'Rejected' },
  requested_for_reverification: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'Re-verification' },
  application_not_completed: { color: '#EBBF37', bgcolor: '#FFFAEC', value: 'Pending' },
  payment_pending: { color: '#EBBF37', bgcolor: '#FFFAEC', value: 'Pending' },
};

export default function RequestTable({
  teacherRequestData,
  // selectionMode = false,
  // selectedIds = new Set(),
  // onSelectOne = () => {},
  // onSelectAll = () => {},
}) {
  const navigate = useNavigate();
  const dataToRender = Array.isArray(teacherRequestData) ? teacherRequestData : [];

  // Helpers for bulk-select
  // Remove all logic related to selectionMode, selectedIds, onSelectOne, onSelectAll, and checkboxes

  const handleCellClick = (event, id) => {
    // Ignore clicks originating from a checkbox
    if (event.target.closest('input[type="checkbox"]')) return;
    // Check if the click is from the actions column
    const isActionsCell = event.target.closest('td:last-child');
    if (!isActionsCell) {
      const basePath = '/admin/request-details/';
      navigate(`${basePath}${id}`);
    }
  };

  function formatText(input) {
    return input
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
  }

  const PopoverLevels = ({ levels, rowId }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl) && levels.length > 0;

    const handlePopoverOpen = event => {
      setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
      setAnchorEl(null);
    };

    return (
      <div
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        className="flex items-center cursor-pointer w-full"
      >
        <Box
          className="flex items-center"
          sx={{ gap: '8px', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}
        >
          {levels.map((level, index) => (
            <Box
              key={index}
              style={{
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px 8px',
                borderRadius: '20px',
                backgroundColor: LevelToTeachColor[level]?.bgcolor,
                color: LevelToTeachColor[level]?.color,
                fontFamily: 'Poppins !important',
                fontSize: '14px !important',
                fontStyle: 'normal !important',
                fontWeight: '400 !important',
                lineHeight: 'normal !important',
                flexShrink: 0,
              }}
            >
              <Typography className="level-text">
                {LevelToTeachColor[level]?.value || '---'}
              </Typography>
            </Box>
          ))}
        </Box>

        {levels.length > 0 && (
          <Popover
            id={`level-popover-${rowId}`}
            sx={{
              pointerEvents: 'none',
              '& .MuiPopover-paper': {
                borderRadius: '8px',
                boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-6px',
                  left: '20px',
                  width: '12px',
                  height: '12px',
                  backgroundColor: 'white',
                  transform: 'rotate(45deg)',
                  borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
                  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                },
              },
            }}
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
            PaperProps={{
              onMouseEnter: handlePopoverOpen,
              onMouseLeave: handlePopoverClose,
              sx: {
                pointerEvents: 'auto',
                minWidth: '150px',
              },
            }}
          >
            <Box
              sx={{
                p: 1,
                '& > :not(:last-child)': {
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              {levels.map((level, index) => (
                <Box
                  key={index}
                  style={{
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px 8px',
                    borderRadius: '20px',
                    backgroundColor: LevelToTeachColor[level]?.bgcolor,
                    color: LevelToTeachColor[level]?.color,
                    fontFamily: 'Poppins !important',
                    fontSize: '12px !important',
                    fontStyle: 'normal !important',
                    fontWeight: '400 !important',
                    marginBottom: '2px',
                  }}
                >
                  <Typography className="level-text">{LevelToTeachColor[level]?.value2}</Typography>
                </Box>
              ))}
            </Box>
          </Popover>
        )}
      </div>
    );
  };

  const PopoverSubjects = ({ subjects, rowId }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl) && subjects.length > 1;

    const handlePopoverOpen = event => {
      if (subjects.length > 1) {
        setAnchorEl(event.currentTarget);
      }
    };

    const handlePopoverClose = () => {
      setAnchorEl(null);
    };

    // Extract subject titles for display
    const subjectTitles = subjects.map(subject =>
      typeof subject === 'object' && subject.title ? subject.title : subject
    );

    return (
      <div
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        className="flex items-center cursor-pointer w-full"
      >
        <Box className="flex items-center" sx={{ gap: '6px' }}>
          <Typography className="subject-text">
            {formatText(
              subjectTitles && subjectTitles.length > 0
                ? subjectTitles.slice(0, 1).join(', ')
                : '---'
            )}
          </Typography>
          {subjects.length > 1 && (
            <Box className="request-subject-count-badge">
              <Typography className="request-subject-count-text">+{subjects.length - 1}</Typography>
            </Box>
          )}
        </Box>

        {subjects.length > 1 && (
          <Popover
            id={`mouse-over-popover-${rowId}`}
            sx={{
              pointerEvents: 'none',
              '& .MuiPopover-paper': {
                overflow: 'visible',
                borderRadius: '8px',
                boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-6px',
                  left: '20px',
                  width: '12px',
                  height: '12px',
                  backgroundColor: 'white',
                  transform: 'rotate(45deg)',
                  borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
                  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                },
              },
            }}
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
            PaperProps={{
              onMouseEnter: handlePopoverOpen,
              onMouseLeave: handlePopoverClose,
              sx: {
                pointerEvents: 'auto',
                width: '250px',
              },
            }}
          >
            <Box
              sx={{
                p: 1,
                '& > :not(:last-child)': {
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              {subjects.map((subject, index) => (
                <Typography
                  key={index}
                  sx={{
                    py: 1,
                    px: 2,
                    fontSize: '12px',
                    fontFamily: 'Poppins',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: '4px',
                    },
                  }}
                >
                  {formatText(
                    typeof subject === 'object' && subject.title ? subject.title : subject
                  )}
                </Typography>
              ))}
            </Box>
          </Popover>
        )}
      </div>
    );
  };

  return (
    <>
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '8px' }}>
        <Table sx={{ minWidth: 650 }} aria-label="paginated table">
          <TableHead
            sx={{
              backgroundColor: '#EEF4FF',
              '& th': {
                color: 'var(--Text, #14171F)',
                height: '25px',
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: '500',
                lineHeight: 'normal',
                padding: '10px 16px',
              },
            }}
          >
            <TableRow>
              {/* Remove checkbox column */}
              <TableCell sx={{ width: '15%' }}>Name</TableCell>
              <TableCell align="left" sx={{ width: '20%' }}>
                Level to Teach
              </TableCell>
              <TableCell align="left" sx={{ width: '20%' }}>
                Subjects
              </TableCell>
              <TableCell align="center" sx={{ width: '15%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Years of Experience
                  {/* <IconButton
                    size="small"
                    onClick={() => handleSortClick('experience')}
                    sx={{
                      padding: '2px',
                      color: 'var(--Text, #14171F)',
                      '&:hover': {
                        backgroundColor: 'rgba(20, 23, 31, 0.04)',
                      },
                    }}
                  >
                    <BiSort fontSize="small" />
                  </IconButton> */}
                </Box>
              </TableCell>
              <TableCell align="left" sx={{ width: '15%' }}>
                Coupon Used
              </TableCell>
              <TableCell align="center" sx={{ width: '15%' }}>
                Status
              </TableCell>
              <TableCell align="left" sx={{ width: '18%', minWidth: 120 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Submit Date
                  {/* <IconButton
                    size="small"
                    onClick={() => handleSortClick('date')}
                    sx={{
                      padding: '2px',
                      color: 'var(--Text, #14171F)',
                      '&:hover': {
                        backgroundColor: 'rgba(20, 23, 31, 0.04)',
                      },
                    }}
                  >
                    <BiSort fontSize="small" />
                  </IconButton> */}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& td': {
                color: 'var(--Text, #14171F) !important',
                height: '25px !important',
                fontFamily: 'Poppins !important',
                fontSize: '14px !important',
                fontStyle: 'normal !important',
                fontWeight: '400 !important',
                lineHeight: 'normal !important',
                padding: '9px 16px',
              },
            }}
          >
            {dataToRender.length === 0 ? (
              <TableRow>
                {/* Remove checkbox cell */}
                <TableCell colSpan={7} align="center">
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              dataToRender.map(row => (
                <TableRow
                  hover
                  // tabIndex={-1}
                  key={row.id}
                  sx={{
                    '& td': {
                      borderBottom: 'none',
                    },
                    '&:hover': {
                      backgroundColor: '#E9F1FF !important',
                      cursor: 'pointer',
                    },
                  }}
                  onClick={e => handleCellClick(e, row.id)}
                >
                  {/* Remove checkbox cell */}
                  <TableCell align="left">
                    <Box className="flex items-center gap-3 font-poppins text-sm font-normal leading-normal">
                      <Avatar
                        src={row?.profile_image || '/broken-image.jpg'}
                        sx={{ width: 25, height: 25 }}
                      />
                      <Typography className="flex name-text">
                        {row.first_name} {row.last_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="left">
                    {Array.isArray(row.level_of_teaching) && row.level_of_teaching.length > 0 ? (
                      <PopoverLevels levels={row.level_of_teaching} rowId={row.id} />
                    ) : (
                      <Typography className="level-text">---</Typography>
                    )}
                  </TableCell>

                  <TableCell align="left">
                    <Box className="flex items-center gap-2">
                      {Array.isArray(row.subjects) && row.subjects.length > 0 ? (
                        <PopoverSubjects subjects={row.subjects} rowId={row.id} />
                      ) : (
                        <Typography className="subject-text">---</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">{row.years_of_experience || '---'}</TableCell>

                  <TableCell align="left">
                    <Box className="flex items-center gap-2">
                      <Typography className="coupon-text">
                        {row?.used_coupon_code ? row?.used_coupon_code : 'No coupon used'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="left">
                    <Box
                      className="status-pill"
                      style={{
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '4px 8px 4px 0px',
                        borderRadius: '20px',
                        backgroundColor: StatusColor[row?.approval_status]?.bgcolor,
                        color: StatusColor[row?.approval_status]?.color,
                        fontFamily: 'Poppins !important',
                        fontSize: '14px !important',
                        fontStyle: 'normal !important',
                        fontWeight: '400 !important',
                        lineHeight: 'normal !important',
                      }}
                    >
                      <Typography
                        className="w-[6px] h-[6px] rounded-full"
                        style={{
                          backgroundColor: StatusColor[row?.approval_status]?.color,
                        }}
                      ></Typography>
                      <Typography className="status-text">
                        {row?.approval_status ? StatusColor[row?.approval_status]?.value : '---'}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell align="left" sx={{ whiteSpace: 'nowrap', minWidth: 120 }}>
                    {formatDate(row?.updated_at)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
